import React, { useMemo, useState } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend,
} from 'recharts';
import {
  useApi, formatNumber, formatPercent, formatDuration,
  channelColor, channelLabel, CHANNEL_LABELS,
} from '../hooks/useApi';
import {
  Card, RangePicker, BarList, EmptyState, ErrorState, TableSkeleton,
} from '../components/ui';

const CHANNEL_KEYS = Object.keys(CHANNEL_LABELS);

export default function Traffic() {
  const [range, setRange] = useState('30d');
  const params = useMemo(() => ({ range }), [range]);

  const channels = useApi('/api/admin/analytics/channels', params);
  const stacked = useApi('/api/admin/analytics/channel-timeseries', params);
  const audience = useApi('/api/admin/analytics/audience', params);
  const pages = useApi('/api/admin/analytics/pages', params);
  const interactions = useApi('/api/admin/analytics/interactions', params);

  const stackData = (stacked.data?.series || []).map((row) => ({
    ...row,
    label: new Date(row.bucket).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
  }));

  // Only stack channels that actually appear, or the legend fills with zeros.
  const activeChannels = CHANNEL_KEYS.filter((key) =>
    stackData.some((row) => (row[key] || 0) > 0));

  if (channels.error) {
    return <ErrorState message={channels.error} onRetry={channels.reload} />;
  }

  return (
    <div className="spa-stack">
      <div className="spa-row">
        <RangePicker value={range} onChange={setRange} />
      </div>

      {/* ------------------------------------------------- channel table */}
      <Card
        title="Channel performance"
        note="Every channel, its volume, and what it produced. Conversion is leads divided by sessions."
        padded={false}
      >
        {channels.isLoading && !channels.data ? (
          <TableSkeleton cols={7} />
        ) : (
          <div className="spa-tablewrap">
            <table className="spa-table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th className="num">Visits</th>
                  <th className="num">Sessions</th>
                  <th className="num">Enquiries</th>
                  <th className="num">Applications</th>
                  <th className="num">Signups</th>
                  <th className="num">Leads</th>
                  <th className="num">Conv.</th>
                  <th className="num">Change</th>
                </tr>
              </thead>
              <tbody>
                {(channels.data?.channels || []).map((c) => (
                  <tr key={c.channel}>
                    <td>
                      <span className="spa-chan" style={{ '--seg': channelColor(c.channel) }}>
                        <span className="spa-chan__dot" aria-hidden="true" />
                        <strong style={{ color: '#14161a' }}>{channelLabel(c.channel)}</strong>
                      </span>
                    </td>
                    <td className="num">{formatNumber(c.visits)}</td>
                    <td className="num">{formatNumber(c.sessions)}</td>
                    <td className="num">{formatNumber(c.enquiries)}</td>
                    <td className="num">{formatNumber(c.applications)}</td>
                    <td className="num">{formatNumber(c.subscribers)}</td>
                    <td className="num" style={{ fontWeight: 600 }}>{formatNumber(c.leads)}</td>
                    <td className="num">{formatPercent(c.conversion_rate, 2)}</td>
                    <td
                      className="num"
                      style={{
                        color: c.leads_change > 0 ? '#067647'
                          : c.leads_change < 0 ? '#d92d20' : '#98a2b3',
                      }}
                    >
                      {c.leads_change > 0 ? '↑' : c.leads_change < 0 ? '↓' : '→'}{' '}
                      {Math.abs(c.leads_change).toFixed(0)}%
                    </td>
                  </tr>
                ))}
              </tbody>
              {channels.data?.totals && (
                <tfoot>
                  <tr style={{ background: '#f7f8fa', fontWeight: 600 }}>
                    <td>All channels</td>
                    <td className="num">{formatNumber(channels.data.totals.visits)}</td>
                    <td className="num">{formatNumber(channels.data.totals.sessions)}</td>
                    <td className="num">{formatNumber(channels.data.totals.enquiries)}</td>
                    <td className="num">{formatNumber(channels.data.totals.applications)}</td>
                    <td className="num">{formatNumber(channels.data.totals.subscribers)}</td>
                    <td className="num">{formatNumber(channels.data.totals.leads)}</td>
                    <td className="num" colSpan={2}>
                      {formatPercent(
                        channels.data.totals.sessions
                          ? (channels.data.totals.leads / channels.data.totals.sessions) * 100
                          : 0, 2
                      )}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>

      {/* --------------------------------------------------- stacked trend */}
      <Card title="Visits by channel over time" note="Stacked, so you can see the mix shift rather than just the total.">
        {stacked.isLoading && !stacked.data ? (
          <div className="spa-skeleton" style={{ height: 280 }} />
        ) : !stackData.length ? (
          <EmptyState icon="fa-chart-column" title="No visits in this period" />
        ) : (
          <div style={{ height: 300 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stackData} margin={{ top: 6, right: 6, left: -18, bottom: 0 }}>
                <CartesianGrid stroke="#e8ebf0" vertical={false} />
                <XAxis
                  dataKey="label" tickLine={false} axisLine={false} minTickGap={26}
                  tick={{ fontSize: 11, fill: '#98a2b3', fontFamily: 'IBM Plex Mono' }}
                />
                <YAxis
                  tickLine={false} axisLine={false}
                  tick={{ fontSize: 11, fill: '#98a2b3', fontFamily: 'IBM Plex Mono' }}
                />
                <Tooltip
                  contentStyle={{
                    border: '1px solid #d8dce5', borderRadius: 5,
                    fontSize: 13, boxShadow: 'none',
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                {activeChannels.map((key) => (
                  <Bar
                    key={key} dataKey={key} name={channelLabel(key)}
                    stackId="ch" fill={channelColor(key)} maxBarSize={34}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* ------------------------------------------------ audience detail */}
      <div className="spa-grid spa-grid--3">
        <Card title="Devices" note="How people reach the site.">
          <BarList items={audience.data?.devices || []} />
        </Card>
        <Card title="Browsers">
          <BarList items={audience.data?.browsers || []} />
        </Card>
        <Card title="Operating systems">
          <BarList items={audience.data?.operating_systems || []} />
        </Card>
      </div>

      <div className="spa-grid spa-grid--2">
        <Card title="Referring sites" note="Who is linking to you.">
          <BarList items={audience.data?.referrers || []} colorBy="channel" />
        </Card>
        <Card title="Campaigns" note="Traffic carrying a utm_campaign tag.">
          {audience.data?.campaigns?.length ? (
            <BarList items={audience.data.campaigns} valueKey="visits" colorBy="channel" />
          ) : (
            <EmptyState
              icon="fa-tag"
              title="No tagged campaigns yet"
              text="Add utm_source, utm_medium and utm_campaign to your ad and email links and they will be broken out here."
            />
          )}
        </Card>
      </div>

      {/* --------------------------------------------------- interactions */}
      <div className="spa-grid spa-grid--2">
        <Card title="What people did" note="Clicks, scroll depth and form activity — not just page views.">
          <BarList items={interactions.data?.by_event || []} />
        </Card>

        <Card
          title="Form completion"
          note="How many people who started a form finished it. A low rate points at a field that is fighting them."
          padded={false}
        >
          {!interactions.data?.form_funnel?.length ? (
            <EmptyState icon="fa-list-check" title="No form activity recorded yet" />
          ) : (
            <div className="spa-tablewrap">
              <table className="spa-table">
                <thead>
                  <tr>
                    <th>Page</th>
                    <th className="num">Started</th>
                    <th className="num">Sent</th>
                    <th className="num">Errors</th>
                    <th className="num">Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {interactions.data.form_funnel.map((f) => (
                    <tr key={f.page}>
                      <td className="spa-table__primary">{f.page}</td>
                      <td className="num">{formatNumber(f.started)}</td>
                      <td className="num">{formatNumber(f.submitted)}</td>
                      <td className="num" style={{ color: f.errored ? '#d92d20' : undefined }}>
                        {formatNumber(f.errored)}
                      </td>
                      <td className="num" style={{
                        color: f.completion_rate >= 60 ? '#067647'
                          : f.completion_rate >= 30 ? '#b54708' : '#d92d20',
                        fontWeight: 600,
                      }}>
                        {formatPercent(f.completion_rate)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>

      {/* ---------------------------------------------------------- pages */}
      <Card title="All pages" note="Ranked by views, with the time people spend and the leads produced." padded={false}>
        {!pages.data?.length ? (
          <TableSkeleton cols={6} />
        ) : (
          <div className="spa-tablewrap">
            <table className="spa-table">
              <thead>
                <tr>
                  <th>Page</th>
                  <th className="num">Views</th>
                  <th className="num">Unique</th>
                  <th className="num">Sessions</th>
                  <th className="num">Avg time</th>
                  <th className="num">Enquiries</th>
                  <th className="num">Applications</th>
                  <th className="num">Conv.</th>
                </tr>
              </thead>
              <tbody>
                {pages.data.map((p) => (
                  <tr key={p.page}>
                    <td className="spa-table__primary">{p.page}</td>
                    <td className="num">{formatNumber(p.views)}</td>
                    <td className="num">{formatNumber(p.unique_visitors)}</td>
                    <td className="num">{formatNumber(p.sessions)}</td>
                    <td className="num">{formatDuration(p.avg_seconds)}</td>
                    <td className="num">{formatNumber(p.enquiries)}</td>
                    <td className="num">{formatNumber(p.applications)}</td>
                    <td className="num">{formatPercent(p.conversion_rate, 2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
